import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }

  code {
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"};
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const EmailCompare = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin: 1.5rem 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const EmailCard = styled.div`
  padding: 1.25rem;
  border-radius: 8px;
  border: 2px solid ${({ $legit }) => ($legit ? "#10b981" : "#ef4444")};
  background-color: ${({ $legit }) =>
    $legit ? "rgba(16, 185, 129, 0.06)" : "rgba(239, 68, 68, 0.06)"};
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${({ $legit }) => ($legit ? "#10b981" : "#ef4444")};
  }

  .field {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;

    span {
      font-weight: 700;
      display: block;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      opacity: 0.7;
    }
  }
`;

const HeaderWalkthrough = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }

  pre {
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.06)"};
    padding: 1rem;
    border-radius: 6px;
    overflow-x: auto;
    font-size: 0.85rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
  }

  ol {
    margin: 1rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.6rem;
      line-height: 1.6;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const EmailSecurityGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.EMAIL_SECURITY);

  // const heroImageSrc = "/images/guides/email-security.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Spotting Spoofed & Compromised Email"
        seoTitle="Email Security Guide: Spot Spoofed & Compromised Email | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Learn how email spoofing works, how to verify a sender's domain, spot account compromise, and identify phishing variants before you click."
      ogTitle="Email Security Guide: Spot Spoofed & Compromised Email | CSFSafe"
      ogDescription="Header analysis, domain verification, and red flags for spoofed or compromised email. Practical guidance for SMB employees."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Email security and spoofing detection"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        Email remains the single biggest way attackers get into small
        businesses—not just through phishing links, but through spoofed sender
        addresses and fully compromised, legitimate accounts. This guide shows
        you how to verify what you're looking at before you trust it.
      </Paragraph>

      <H2>Email Spoofing Basics</H2>

      <Paragraph>
        Spoofing means forging the "From" field so an email appears to come
        from someone it didn't. Because the core email protocol (SMTP) wasn't
        built with authentication in mind, anyone can put any name and address
        in the "From" line. Modern email systems use three technical checks—
        <strong> SPF</strong>, <strong>DKIM</strong>, and{" "}
        <strong>DMARC</strong>—to help catch this, but they aren't foolproof
        and not every organization enforces them strictly.
      </Paragraph>

      <H3>SPF, DKIM, and DMARC</H3>

      <CalloutBox $type="tip">
        <strong>The Three Authentication Checks, in Plain English</strong>
        <ul>
          <li>
            <strong>SPF (Sender Policy Framework):</strong> Confirms the
            sending mail server is authorized to send email for that domain
          </li>
          <li>
            <strong>DKIM (DomainKeys Identified Mail):</strong> A digital
            signature proving the message wasn't altered in transit
          </li>
          <li>
            <strong>DMARC (Domain-based Message Authentication):</strong>
            Tells receiving servers what to do if SPF/DKIM checks fail
            (quarantine, reject, or allow)
          </li>
          <li>
            A message can still reach your inbox even if it fails these
            checks—many organizations don't enforce strict DMARC policies
          </li>
        </ul>
      </CalloutBox>

      <H2>Domain Verification: Look Closely</H2>

      <Paragraph>
        Attackers rely on you skimming instead of reading carefully. Slow down
        and check the actual domain, not just the display name.
      </Paragraph>

      <H3>Tricks Attackers Use</H3>

      <CalloutBox $type="warning">
        <strong>Common Domain Tricks</strong>
        <ul>
          <li>
            <strong>Lookalike characters:</strong> <code>rnicrosoft.com</code>{" "}
            (rn instead of m), <code>amaz0n.com</code> (zero instead of "o")
          </li>
          <li>
            <strong>Extra subdomains:</strong> <code>
              paypal.com.secure-verify.net
            </code>{" "}
            — the real domain is "secure-verify.net," not PayPal
          </li>
          <li>
            <strong>Different top-level domain:</strong>{" "}
            <code>yourbank.co</code> instead of <code>yourbank.com</code>
          </li>
          <li>
            <strong>Display name spoofing:</strong> The name shown is "Jane
            Smith, CEO" but the actual address is a random Gmail account
          </li>
          <li>
            <strong>Reply-to mismatch:</strong> The "From" address looks right
            but the "Reply-To" field routes responses elsewhere—check both
          </li>
        </ul>
      </CalloutBox>

      <H2>Header Analysis Walkthrough</H2>

      <HeaderWalkthrough>
        <h3>How to Check Full Email Headers</h3>
        <Paragraph>
          Every email carries hidden routing information in its headers. Most
          email clients let you view this via "Show Original," "View Source,"
          or "Message Details."
        </Paragraph>
        <pre>{`Received: from mail-relay.suspicious-host.net (unverified [203.0.113.44])
        by yourcompany-mail.com; Mon, 20 Jul 2026 09:14:02 -0400
From: "Accounts Payable" <billing@yourcompany.com>
Reply-To: ap-billing@fastpay-invoices.net
Authentication-Results: spf=fail smtp.mailfrom=fastpay-invoices.net;
                         dkim=fail; dmarc=fail`}</pre>
        <ol>
          <li>
            <strong>Check "Authentication-Results":</strong> Look for{" "}
            <code>spf=fail</code>, <code>dkim=fail</code>, or{" "}
            <code>dmarc=fail</code>—any failure is a red flag
          </li>
          <li>
            <strong>Trace "Received" lines from bottom to top:</strong> These
            show the actual path the email traveled. A mismatch between the
            claimed sender and the originating server is suspicious
          </li>
          <li>
            <strong>Compare "From" and "Reply-To":</strong> If they point to
            different domains, treat the message as suspicious
          </li>
          <li>
            <strong>Check the originating IP:</strong> Look it up—an email
            claiming to be from your own company shouldn't originate from an
            unfamiliar hosting provider overseas
          </li>
        </ol>
      </HeaderWalkthrough>

      <H2>Real Email Examples: Legitimate vs. Suspicious</H2>

      <EmailCompare>
        <EmailCard $legit>
          <h4>
            <FaCheckCircle /> Legitimate
          </h4>
          <div className="field">
            <span>From</span>billing@vendor-realcompany.com
          </div>
          <div className="field">
            <span>Subject</span>Your invoice #4471 is ready
          </div>
          <div className="field">
            <span>Body</span>Addressed to you by name, references an actual
            account number, link points to vendor-realcompany.com
          </div>
          <div className="field">
            <span>Tone</span>No urgency, no threats, matches prior
            correspondence
          </div>
        </EmailCard>
        <EmailCard>
          <h4>
            <FaTimesCircle /> Suspicious
          </h4>
          <div className="field">
            <span>From</span>billing@vendor-realcompany-invoices.net
          </div>
          <div className="field">
            <span>Subject</span>URGENT: Invoice Overdue — Action Required
            Today
          </div>
          <div className="field">
            <span>Body</span>"Dear Customer," generic greeting, link goes to a
            shortened URL
          </div>
          <div className="field">
            <span>Tone</span>Urgent language, threatens account suspension,
            requests login to "verify"
          </div>
        </EmailCard>
      </EmailCompare>

      <H2>Red Flags Checklist</H2>

      <ChecklistSection>
        <h3>Before You Trust an Email, Check For</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            Sender domain matches exactly what you'd expect (no extra words,
            hyphens, or lookalike characters)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            "Reply-To" address matches the "From" domain
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            No urgent, threatening, or secrecy language pressuring quick action
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Links, when hovered, point to the domain they claim to
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            No unexpected requests for credentials, payment changes, or gift
            cards
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            Attachment types are expected (not .exe, .scr, or macro-enabled
            files you didn't ask for)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Message tone and formatting matches how this sender normally
            writes
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Signs Your Own Account Has Been Compromised</H2>

      <CalloutBox $type="warning">
        <strong>Watch for These Warning Signs</strong>
        <ul>
          <li>Sent items you don't recognize, or contacts report odd emails from you</li>
          <li>New forwarding rules or filters you didn't create</li>
          <li>Login alerts from unfamiliar locations or devices</li>
          <li>Missing emails, or emails marked read that you never opened</li>
          <li>Password reset emails for accounts you didn't request</li>
          <li>Changes to your signature, display name, or recovery email/phone</li>
        </ul>
      </CalloutBox>

      <CalloutBox $type="critical">
        <strong>If You Suspect Compromise, Act Now</strong>
        <ul>
          <li>Change your password immediately from a clean, trusted device</li>
          <li>Enable or re-verify multi-factor authentication</li>
          <li>Check and remove any unfamiliar forwarding rules or filters</li>
          <li>Review "Sent" and "Deleted" folders for signs of misuse</li>
          <li>Review connected apps and revoke access for anything unrecognized</li>
          <li>Notify your IT/security team so they can check for lateral movement</li>
        </ul>
      </CalloutBox>

      <H2>Common Phishing Variants to Recognize</H2>

      <Paragraph>
        <strong>Business Email Compromise (BEC):</strong> Impersonates an
        executive or vendor to request a wire transfer or payment change.
        Often uses a lookalike domain, not a hacked account.
      </Paragraph>
      <Paragraph>
        <strong>Invoice/Payment Fraud:</strong> Attacker inserts themselves
        into a legitimate email thread (sometimes after compromising a
        vendor's account) and asks you to redirect payment to new banking
        details.
      </Paragraph>
      <Paragraph>
        <strong>Account Compromise Follow-Through:</strong> Once an account is
        hacked, attackers send phishing to your entire contact list using your
        real, trusted address—no spoofing required, which makes it especially
        convincing.
      </Paragraph>
      <Paragraph>
        <strong>Thread Hijacking:</strong> Attacker replies within an existing
        email conversation with a malicious link, exploiting the trust already
        built in that thread.
      </Paragraph>

      <H2>Safe Practices</H2>

      <CalloutBox $type="do">
        <strong>DO These Things</strong>
        <ul>
          <li>
            Verify payment or banking detail changes by phone, using a number
            you already have on file—not one in the email
          </li>
          <li>Enable MFA on all email accounts, especially admin accounts</li>
          <li>Turn on SPF, DKIM, and DMARC enforcement for your own domain</li>
          <li>Check full headers when something feels off, even slightly</li>
          <li>Report suspicious emails to IT instead of just deleting them</li>
          <li>Keep your email client and browser updated</li>
        </ul>
      </CalloutBox>

      <CalloutBox $type="dont">
        <strong>DON'T Do These Things</strong>
        <ul>
          <li>Don't trust the display name alone—always check the full address</li>
          <li>Don't click "verify your account" links from unexpected emails</li>
          <li>Don't change payment details based on an email request alone</li>
          <li>Don't reuse your email password on other sites</li>
          <li>Don't assume a reply within an existing thread is automatically safe</li>
        </ul>
      </CalloutBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.PHISHING_DETECTION)}>
            Phishing Detection Quick Guide
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.SPOOFING)}>
            Spoofing Threat Type: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.BUSINESS_EMAIL_COMPROMISE)}>
            Business Email Compromise (BEC)
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.EMAIL_ACCOUNT_PROTECTION)}>
            Email Account Protection
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/email-security"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

EmailSecurityGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default EmailSecurityGuide;
